import React, { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";

export function FloatingText3D({ children, className = "", dark, delay = 0 }) {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (textRef.current) {
        const rect = textRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
        const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
        setMousePosition({ x, y });
      }
    };

    const element = textRef.current;
    if (element) {
      element.addEventListener('mousemove', handleMouseMove);
      element.addEventListener('mouseenter', () => setIsHovered(true));
      element.addEventListener('mouseleave', () => setIsHovered(false));
    }

    return () => {
      if (element) {
        element.removeEventListener('mousemove', handleMouseMove);
        element.removeEventListener('mouseenter', () => setIsHovered(true));
        element.removeEventListener('mouseleave', () => setIsHovered(false));
      }
    };
  }, []);

  return (
    <motion.div
      ref={textRef}
      className={`relative cursor-pointer ${className}`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.8 }}
      whileHover={{ scale: 1.05 }}
    >
      {/* Main text with 3D effect */}
      <motion.h2
        className={`text-4xl md:text-6xl font-bold relative z-10 ${
          dark ? 'text-white' : 'text-[#1b1c4e]'
        }`}
        animate={{
          x: mousePosition.x * 10,
          y: mousePosition.y * 10,
          rotateX: mousePosition.y * 20,
          rotateY: mousePosition.x * 20,
        }}
        transition={{ type: "spring", stiffness: 100, damping: 10 }}
      >
        {children}
      </motion.h2>

      {/* 3D shadow layers */}
      {[...Array(5)].map((_, i) => (
        <motion.h2
          key={i}
          className={`absolute top-0 left-0 text-4xl md:text-6xl font-bold ${
            dark ? 'text-indigo-400/20' : 'text-indigo-600/20'
          }`}
          style={{
            zIndex: 5 - i,
            transform: `translateZ(${-i * 2}px)`,
          }}
          animate={{
            x: mousePosition.x * (10 + i * 2),
            y: mousePosition.y * (10 + i * 2),
            rotateX: mousePosition.y * (20 + i * 5),
            rotateY: mousePosition.x * (20 + i * 5),
          }}
          transition={{ type: "spring", stiffness: 100, damping: 10 }}
        >
          {children}
        </motion.h2>
      ))}

      {/* Glow effect */}
      <motion.div
        className={`absolute inset-0 rounded-lg blur-xl opacity-0 ${
          dark ? 'bg-indigo-400/30' : 'bg-indigo-600/30'
        }`}
        animate={{
          opacity: isHovered ? 0.8 : 0,
          scale: isHovered ? 1.2 : 1,
        }}
        transition={{ duration: 0.3 }}
      />
    </motion.div>
  );
}

export function TypingText({ text, speed = 100, className = "", dark }) {
  const [displayText, setDisplayText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);

      return () => clearTimeout(timer);
    }
  }, [currentIndex, text, speed]);

  return (
    <motion.div
      className={`text-2xl md:text-4xl font-mono ${className} ${
        dark ? 'text-indigo-400' : 'text-indigo-600'
      }`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {displayText}
      <motion.span
        animate={{ opacity: [1, 0, 1] }}
        transition={{ duration: 1, repeat: Infinity }}
        className="ml-1"
      >
        |
      </motion.span>
    </motion.div>
  );
}

export function GlitchText({ children, className = "", dark, delay = 0 }) {
  const [isGlitching, setIsGlitching] = useState(false);

  useEffect(() => {
    const glitchInterval = setInterval(() => {
      setIsGlitching(true);
      setTimeout(() => setIsGlitching(false), 200);
    }, 3000);

    return () => clearInterval(glitchInterval);
  }, []);

  return (
    <motion.div
      className={`relative ${className}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.8 }}
    >
      <motion.h3
        className={`text-2xl md:text-3xl font-bold relative z-10 ${
          dark ? 'text-white' : 'text-[#1b1c4e]'
        }`}
        animate={isGlitching ? {
          x: [0, -2, 2, -1, 1, 0],
          y: [0, 1, -1, 0],
        } : {}}
        transition={{ duration: 0.2 }}
      >
        {children}
      </motion.h3>

      {/* Glitch layers */}
      <motion.h3
        className={`absolute top-0 left-0 text-2xl md:text-3xl font-bold text-red-400 opacity-0 ${
          isGlitching ? 'opacity-80' : ''
        }`}
        animate={isGlitching ? {
          x: [0, 2, -2, 1, -1, 0],
          y: [0, -1, 1, 0],
        } : {}}
        transition={{ duration: 0.2 }}
      >
        {children}
      </motion.h3>

      <motion.h3
        className={`absolute top-0 left-0 text-2xl md:text-3xl font-bold text-blue-400 opacity-0 ${
          isGlitching ? 'opacity-80' : ''
        }`}
        animate={isGlitching ? {
          x: [0, -1, 1, -2, 2, 0],
          y: [0, 1, -1, 0],
        } : {}}
        transition={{ duration: 0.2 }}
      >
        {children}
      </motion.h3>
    </motion.div>
  );
} 